/**
 * Emoji Mapping System for Mini Product Cards
 * 
 * Maps product categories/subcategories to representative emojis
 * Used only for mini cards (non-hero cards)
 * 
 * PRIORITY: More specific subcategory matches first, then category fallbacks
 * Goal: Maximum diversity - each subcategory should have a unique emoji when possible
 */

/**
 * Get emoji for product based on category/subcategory
 */
export function getEmojiForProduct(category?: string, subcategory?: string): string {
  const subcatLower = (subcategory || '').toLowerCase()
  const catLower = (category || '').toLowerCase()
  
  // ===== WALK ESSENTIALS & GEAR =====
  // Harnesses - specific types
  if (subcatLower.includes('harness') && subcatLower.includes('no-pull')) {
    return '🛡️' // Shield for safety/no-pull harnesses
  }
  if (subcatLower.includes('harness') && subcatLower.includes('front-clip')) {
    return '🎯' // Target for front-clip harnesses
  }
  if (subcatLower.includes('harness')) {
    return '🦮' // Guide dog with harness
  }
  
  // Leashes - specific types
  if (subcatLower.includes('leash') && subcatLower.includes('retractable')) {
    return '📏' // Ruler for retractable leash
  }
  if (subcatLower.includes('leash') || subcatLower.includes('lead')) {
    return '🪢' // Knot for leash
  }
  
  // Collars - specific types
  if (subcatLower.includes('collar') && subcatLower.includes('training')) {
    return '⭕' // Circle/collar for training collars
  }
  if (subcatLower.includes('collar') && (subcatLower.includes('led') || subcatLower.includes('light'))) {
    return '💡' // Light bulb for LED collars
  }
  if (subcatLower.includes('collar') && subcatLower.includes('reflective')) {
    return '✨' // Sparkles for reflective
  }
  if (subcatLower.includes('collar')) {
    return '⭕' // Circle for collar
  }
  
  // ===== HEALTH & WELLNESS =====
  // CRITICAL: Check health/medication categories BEFORE ID tags to avoid false matches
  // (e.g., "kidney" contains "id" but should get medication emoji, not tag emoji)
  
  // Medications & Prescriptions - MUST come before ID tag check
  if (subcatLower.includes('kidney') || subcatLower.includes('renal')) {
    return '🫘' // Kidney bean for kidney health
  }
  if (subcatLower.includes('medication') || subcatLower.includes('medicine')) {
    return '💊' // Pill for medications
  }
  if (subcatLower.includes('prescription') || subcatLower.includes('rx')) {
    return '💉' // Syringe for prescriptions
  }
  if (subcatLower.includes('pharmacy')) {
    return '💉' // Syringe
  }
  
  // Flea & Tick
  if (subcatLower.includes('flea') || subcatLower.includes('tick')) {
    return '🪲' // Beetle for pests
  }
  
  // Vitamins & Supplements
  if (subcatLower.includes('vitamin') || subcatLower.includes('supplement')) {
    return '💊' // Pill
  }
  
  // Dental Care
  if (subcatLower.includes('dental') || subcatLower.includes('tooth')) {
    return '🦷' // Tooth
  }
  
  // Joint Support
  if (subcatLower.includes('joint') || subcatLower.includes('hip') || subcatLower.includes('mobility')) {
    return '🦴' // Bone for joint support
  }
  
  // Calming/Anxiety
  if (subcatLower.includes('calming') || subcatLower.includes('anxiety')) {
    return '🌙' // Moon for calming/relaxation
  }
  
  // Allergy & Itch Relief
  if (subcatLower.includes('allergy') || subcatLower.includes('itch')) {
    return '🌿' // Herb for natural relief
  }
  
  // Heartworm
  if (subcatLower.includes('heartworm')) {
    return '❤️' // Heart
  }
  
  // ID Tags - CRITICAL: Match exact patterns to avoid false matches (e.g., "kidney" contains "id")
  // Must be exact match for "id tag", "id-tag", "id_tag", or standalone "tag" (not part of other words)
  // This check comes AFTER medication checks to prevent false matches
  if (
    subcatLower === 'id tag' || 
    subcatLower === 'id-tag' || 
    subcatLower === 'id_tag' ||
    subcatLower === 'idtags' ||
    subcatLower === 'tags' ||
    (subcatLower.includes('id tag') && !subcatLower.includes('kidney') && !subcatLower.includes('fluid')) ||
    (subcatLower.includes('id-tag') && !subcatLower.includes('kidney') && !subcatLower.includes('fluid')) ||
    (subcatLower.includes('id_tag') && !subcatLower.includes('kidney') && !subcatLower.includes('fluid'))
  ) {
    return '🏷️' // Tag emoji
  }
  
  // Tie-outs
  if (subcatLower.includes('tie-out') || subcatLower.includes('tieout') || subcatLower.includes('tether')) {
    return '🔗' // Link chain for tie-out
  }
  
  // Service Dog Vests
  if (subcatLower.includes('service') || subcatLower.includes('vest')) {
    return '🦺' // Safety vest
  }
  
  // Head Collars/Halters
  if (subcatLower.includes('head collar') || subcatLower.includes('halter')) {
    return '🔗' // Chain link for head collar/halter
  }
  
  // Muzzles
  if (subcatLower.includes('muzzle')) {
    return '🔒' // Lock for muzzle/restraint
  }
  
  // Poop Bags
  if (subcatLower.includes('poop bag') || subcatLower.includes('waste bag') || subcatLower.includes('poop')) {
    return '💩' // Poop emoji
  }
  
  // ===== TOYS & PLAY =====
  // Puzzle Toys
  if (subcatLower.includes('puzzle')) {
    return '🧩' // Puzzle piece
  }
  
  // Treat Dispensing Toys
  if (subcatLower.includes('treat dispenser') || subcatLower.includes('dispensing')) {
    return '🎁' // Gift box
  }
  
  // Balls
  if (subcatLower.includes('ball') && subcatLower.includes('tennis')) {
    return '🎾' // Tennis ball
  }
  if (subcatLower.includes('ball')) {
    return '⚽' // Soccer ball
  }
  
  // Rope & Tug Toys
  if (subcatLower.includes('rope') || subcatLower.includes('tug')) {
    return '🪢' // Knot
  }
  
  // Plush Toys
  if (subcatLower.includes('plush')) {
    return '🧸' // Teddy bear
  }
  
  // Chew Toys
  if (subcatLower.includes('chew toy') || subcatLower.includes('chew')) {
    return '🦴' // Bone
  }
  
  // Fetch Toys
  if (subcatLower.includes('fetch')) {
    return '🥏' // Flying disc
  }
  
  // Training Toys
  if (subcatLower.includes('training') && subcatLower.includes('toy')) {
    return '🎯' // Target
  }
  
  // General Toys
  if (subcatLower.includes('toy') || subcatLower.includes('play')) {
    return '🎪' // Circus tent
  }
  
  // ===== FOOD & TREATS =====
  // Treats - specific types
  if (subcatLower.includes('dental treat')) {
    return '🦷' // Tooth for dental treats
  }
  if (subcatLower.includes('training treat')) {
    return '⭐' // Star for training rewards
  }
  if (subcatLower.includes('treat') || subcatLower.includes('snack')) {
    return '🦴' // Bone for treats
  }
  
  // Food - specific types
  if (subcatLower.includes('wet food')) {
    return '🥫' // Canned food
  }
  if (subcatLower.includes('dry food')) {
    return '🍖' // Meat on bone
  }
  if (subcatLower.includes('fresh food')) {
    return '🥩' // Cut of meat
  }
  if (subcatLower.includes('food') || subcatLower.includes('meal')) {
    return '🍽️' // Fork and knife
  }
  
  // ===== BEDS & COMFORT =====
  // Orthopedic Beds
  if (subcatLower.includes('orthopedic') || subcatLower.includes('bed')) {
    return '🛏️' // Bed
  }
  
  // Crates & Kennels
  if (subcatLower.includes('crate') || subcatLower.includes('kennel')) {
    return '📦' // Package/box
  }
  
  // Blankets
  if (subcatLower.includes('blanket')) {
    return '🛋️' // Couch
  }
  
  // ===== CLOTHING & WEATHER PROTECTION =====
  // Rain Gear
  if (subcatLower.includes('rain') || subcatLower.includes('rainy') || subcatLower.includes('raincoat') || subcatLower.includes('waterproof')) {
    return '☔' // Umbrella for rain gear
  }
  
  // Cold Weather - jackets, pullovers, sweaters
  if (subcatLower.includes('cold') || subcatLower.includes('winter') || subcatLower.includes('coat') || subcatLower.includes('sweater') || subcatLower.includes('pullover') || subcatLower.includes('jacket')) {
    return '🧥' // Coat/jacket for cold weather gear
  }
  
  // Boots/Paw Protection
  if (subcatLower.includes('boot') || subcatLower.includes('paw protection')) {
    return '🐾' // Paw print for paw protection
  }
  
  // Cooling Gear
  if (subcatLower.includes('cooling') || subcatLower.includes('summer') || subcatLower.includes('heat') || subcatLower.includes('vest')) {
    return '🧊' // Ice cube
  }
  
  // Sun Protection
  if (subcatLower.includes('sunscreen') || subcatLower.includes('sun protection')) {
    return '☀️' // Sun
  }
  
  // General Clothing
  if (subcatLower.includes('clothing') || subcatLower.includes('pajama') || subcatLower.includes('apparel')) {
    return '🧥' // Coat/jacket for dog clothing
  }
  
  // ===== GROOMING =====
  // Brushes
  if (subcatLower.includes('brush') || subcatLower.includes('comb')) {
    return '🪮' // Hair pick (tool, not human-specific)
  }
  
  // Shampoo
  if (subcatLower.includes('shampoo') || subcatLower.includes('conditioner')) {
    return '🧴' // Lotion bottle
  }
  
  // Nail Care
  if (subcatLower.includes('nail') || subcatLower.includes('paw care')) {
    return '✂️' // Scissors
  }
  
  // Ear Care
  if (subcatLower.includes('ear')) {
    return '💧' // Droplet for ear cleaning solution
  }
  
  // General Grooming
  if (subcatLower.includes('grooming')) {
    return '🪒' // Razor for grooming tools
  }
  
  // ===== TRAVEL & CARRIERS =====
  if (subcatLower.includes('travel') || subcatLower.includes('carrier')) {
    return '✈️' // Airplane
  }
  
  // ===== BOWLS & FEEDERS =====
  if (subcatLower.includes('bowl') || subcatLower.includes('feeder')) {
    return '🥣' // Bowl for dog bowls/feeders
  }
  
  if (subcatLower.includes('water') || subcatLower.includes('fountain')) {
    return '💧' // Droplet
  }
  
  // ===== CATEGORY-LEVEL FALLBACKS =====
  // Check category if subcategory didn't match
  if (catLower.includes('health') || catLower.includes('wellness')) {
    return '💊'
  }
  if (catLower.includes('vitamin') || catLower.includes('supplement')) {
    return '💊'
  }
  if (catLower.includes('flea') || catLower.includes('tick')) {
    return '🪲'
  }
  if (catLower.includes('treat')) {
    return '🦴'
  }
  if (catLower.includes('food')) {
    return '🍽️'
  }
  if (catLower.includes('toy')) {
    return '🎪'
  }
  if (catLower.includes('harness')) {
    return '🦮'
  }
  if (catLower.includes('leash')) {
    return '🪢'
  }
  if (catLower.includes('collar')) {
    return '⭕'
  }
  if (catLower.includes('bed')) {
    return '🛏️'
  }
  if (catLower.includes('crate')) {
    return '📦'
  }
  if (catLower.includes('grooming')) {
    return '🪒' // Razor for grooming
  }
  
  // Default fallback - use paw print instead of generic dog
  return '🐾'
}

